import { authService, authFetch } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

function authHeader(): HeadersInit {
  const t = authService.getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function get<T>(path: string): Promise<T> {
  const resp = await authFetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      detail = body.detail || body.error || detail;
    } catch { /* ignore */ }
    throw new Error(`Dashboard ${path} failed: ${detail}`);
  }
  return resp.json() as Promise<T>;
}

// Backend client_id values match the MCP server's clientConfig.ts keys.
// The frontend's DashboardClientId is a separate display-oriented enum, so
// callers must translate (mapBackendClientId below).
export type BackendClientId = 'dwa-internal' | 'morningstar' | 'plc';

// ─── Portfolios ──────────────────────────────────────────────────────────────

export interface PortfolioDef {
  key: string;
  label: string;
  dynamic: boolean;
  community_uniques: string[];
}

export function fetchPortfolios(clientId: BackendClientId): Promise<PortfolioDef[]> {
  return get<PortfolioDef[]>(`/dashboard/${clientId}/portfolios`);
}

export function mapBackendClientId(displayId: string): BackendClientId {
  // Accepts either the long display form (priority-life-care) or the
  // already-backend form (plc) so callers don't have to know which they're
  // holding. Previously the default fall-through silently returned
  // 'dwa-internal' for any unknown input, which surfaced as the LeadSources
  // panel on PLC's dashboard calling /dashboard/dwa-internal/leads/sources.
  switch (displayId) {
    case 'morningstar-senior-living':
    case 'morningstar':
      return 'morningstar';
    case 'priority-life-care':
    case 'plc':
      return 'plc';
    case 'dwa-internal':
      return 'dwa-internal';
    default:
      throw new Error(`mapBackendClientId: unrecognized clientId '${displayId}'`);
  }
}

/** Reverse of mapBackendClientId: backend id → frontend display id. */
export function mapDisplayClientId(backendId: BackendClientId): string {
  switch (backendId) {
    case 'morningstar':
      return 'morningstar-senior-living';
    case 'plc':
      return 'priority-life-care';
    case 'dwa-internal':
    default:
      return 'dwa-internal';
  }
}

// ─── Response types (match dashboard_routes.py exactly) ──────────────────────

export interface DashboardOverview {
  client_id: string;
  total_communities: number;
  total_units: number;
  occupied_units: number;
  vacant_units: number;
  occupancy_rate: number;
  by_care_group: Record<string, { total: number; occupied: number; vacant: number }>;
  active_residents: number;
  recent_move_ins_30d: number;
  active_prospects: number;
  last_snapshot_at: string | null;
}

export interface DashboardCommunity {
  community_id: number;
  community_unique: string;
  name: string;
  abbreviation: string | null;
  care_type: string | null;
  total_units: number;
  occupied_units: number;
  vacant_units: number;
  occupancy_rate: number;
}

export interface DashboardMoveIn {
  contract_id: number;
  individual_id: number | null;
  individual_name: string | null;
  case_number: string | null;
  community_unique: string | null;
  community_id: number | null;
  unit_number: string | null;
  care_group: string | null;
  floor_plan: string | null;
  base_monthly: number | null;
  move_in_date: string | null;
}

export interface DashboardLead {
  case_id: number;
  case_number: string | null;
  individual_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  mobile_phone: string | null;
  individual_type: string | null;
  status_name: string | null;
  assigned_user: string | null;
  created_by_user: string | null;
  community_unique: string | null;
  community_id: number | null;
  date_created: string | null;
}

export interface CurrentStateMetrics {
  occupancy_rate: number;
  occupied_units: number;
  total_units: number;
  active_leads: number;
  move_ins_in_window: number;
  move_outs_in_window: number;
  net_move_change: number;
}

export interface WeeklyCommunity {
  community_unique: string;
  community_name: string | null;
  community_abbreviation: string | null;
  leads_tw: number;
  leads_pw: number;
  leads_wow_pct: number | null;
  leads_wow_delta: number;
  tours_tw: number;
  tours_pw: number;
  tours_wow_pct: number | null;
  tours_wow_delta: number;
  fu_tw: number;
  fu_pw: number;
  fu_rate_tw: number;
  fu_rate_pw: number;
  fu_rate_delta_pp: number;
}

export interface TrendWeek {
  week_start: string;
  week_end: string;
  leads: number;
  tours: number;
  fu_count: number;
  fu_rate: number;
}

export interface TrendOccupancyPoint {
  snapshot_date: string;
  occupied_units: number;
  total_units: number;
  occupancy_rate: number;
}

export interface DashboardTrends {
  client_id: string;
  filter?: { community_unique: string | null };
  weeks: TrendWeek[];
  occupancy: TrendOccupancyPoint[];
  series_start: string;
  series_end: string;
}

export interface MonthlyCommunity {
  community_unique: string;
  community_name: string | null;
  community_abbreviation: string | null;
  leads_tm: number;
  leads_pm: number;
  leads_mom_pct: number | null;
  leads_mom_delta: number;
  tours_tm: number;
  tours_pm: number;
  tours_mom_pct: number | null;
  tours_mom_delta: number;
  fu_tm: number;
  fu_pm: number;
  fu_rate_tm: number;
  fu_rate_pm: number;
  fu_rate_delta_pp: number;
}

export interface DashboardMonthly {
  client_id: string;
  /** 'mom' (default) or 'yoy' — echoes the requested comparison mode. */
  compare?: 'mom' | 'yoy';
  snapshot_at: string | null;
  this_month: { start: string; end: string };
  /** Prior period; semantics depend on `compare` (last month vs same month last year). */
  prior_month: { start: string; end: string };
  filter?: { portfolio: string | null; community_unique: string | null };
  totals: {
    leads_tm: number;
    leads_pm: number;
    leads_mom_pct: number | null;
    leads_mom_delta: number;
    tours_tm: number;
    tours_pm: number;
    tours_mom_pct: number | null;
    tours_mom_delta: number;
    fu_rate_tm: number;
    fu_rate_pm: number;
    fu_rate_delta_pp: number;
  };
  top_leads: MonthlyCommunity[];
  top_tours: MonthlyCommunity[];
  biggest_gain: MonthlyCommunity[];
  biggest_decline: MonthlyCommunity[];
  communities: MonthlyCommunity[];
  current_state: CurrentStateMetrics;
}

export interface CustomCommunity {
  community_unique: string;
  community_name: string | null;
  community_abbreviation: string | null;
  leads_cur: number;
  leads_pri: number;
  leads_change_pct: number | null;
  leads_change_delta: number;
  tours_cur: number;
  tours_pri: number;
  tours_change_pct: number | null;
  tours_change_delta: number;
  fu_cur: number;
  fu_pri: number;
  fu_rate_cur: number;
  fu_rate_pri: number;
  fu_rate_delta_pp: number;
}

export interface DashboardCustom {
  client_id: string;
  snapshot_at: string | null;
  this_period: { start: string; end: string };
  prior_period: { start: string; end: string };
  filter?: { portfolio: string | null; community_unique: string | null };
  totals: {
    leads_cur: number;
    leads_pri: number;
    leads_change_pct: number | null;
    leads_change_delta: number;
    tours_cur: number;
    tours_pri: number;
    tours_change_pct: number | null;
    tours_change_delta: number;
    fu_rate_cur: number;
    fu_rate_pri: number;
    fu_rate_delta_pp: number;
  };
  communities: CustomCommunity[];
  current_state: CurrentStateMetrics;
}

export interface DashboardWeekly {
  client_id: string;
  snapshot_at: string | null;
  this_week: { start: string; end: string };
  prior_week: { start: string; end: string };
  /** Echoed back from query params — currently only `community_unique`. */
  filter?: { community_unique: string | null };
  totals: {
    leads_tw: number;
    leads_pw: number;
    leads_wow_pct: number | null;
    leads_wow_delta: number;
    tours_tw: number;
    tours_pw: number;
    tours_wow_pct: number | null;
    tours_wow_delta: number;
    fu_rate_tw: number;
    fu_rate_pw: number;
    fu_rate_delta_pp: number;
  };
  top_leads: WeeklyCommunity[];
  top_tours: WeeklyCommunity[];
  biggest_gain: WeeklyCommunity[];
  biggest_decline: WeeklyCommunity[];
  communities: WeeklyCommunity[];
  current_state: CurrentStateMetrics;
}

// ─── Endpoint wrappers ───────────────────────────────────────────────────────
// NOTE: paths here do NOT include /api/ — VITE_API_URL already supplies it
// (/api in prod, http://localhost:8080 in dev). The browser fetch URL ends up
// as /api/dashboard/... → nginx strips /api/ → agent receives /dashboard/...

export function fetchOverview(clientId: BackendClientId): Promise<DashboardOverview> {
  return get<DashboardOverview>(`/dashboard/${clientId}/overview`);
}

export function fetchCommunities(clientId: BackendClientId): Promise<DashboardCommunity[]> {
  return get<DashboardCommunity[]>(`/dashboard/${clientId}/communities`);
}

export function fetchMoveIns(
  clientId: BackendClientId,
  windowDays = 30,
  limit = 50,
): Promise<DashboardMoveIn[]> {
  return get<DashboardMoveIn[]>(
    `/dashboard/${clientId}/move-ins?windowDays=${windowDays}&limit=${limit}`,
  );
}

export function fetchLeads(
  clientId: BackendClientId,
  options: { limit?: number; status?: string; communityUnique?: string } = {},
): Promise<DashboardLead[]> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.status) params.set('status', options.status);
  if (options.communityUnique) params.set('communityUnique', options.communityUnique);
  const qs = params.toString();
  return get<DashboardLead[]>(`/dashboard/${clientId}/leads${qs ? `?${qs}` : ''}`);
}

/**
 * Filter shape used by PLC. Maps to the backend's two-axis filter
 * (Ownership Group × Community Group) plus an optional drill-down
 * to a single community. Composed alongside the legacy `portfolio` /
 * `communityUnique` MorningStar params — only the relevant ones get
 * serialized based on which client is calling.
 */
export interface PLCFilter {
  ownershipGroup?: string;
  communityGroup?: string;
  communityId?: number;
}

interface WeeklyQsOptions {
  referenceDate?: string;
  // MorningStar-shape filters
  portfolio?: string;
  communityUnique?: string;
  // PLC-shape filters
  ownershipGroup?: string;
  communityGroup?: string;
  communityId?: number;
}

interface MonthlyQsOptions extends WeeklyQsOptions {
  /** 'mom' (default, prior month) or 'yoy' (same month last year). */
  compare?: 'mom' | 'yoy';
}

function _appendCommonScopeParams(params: URLSearchParams, opts: WeeklyQsOptions): void {
  if (opts.portfolio) params.set('portfolio', opts.portfolio);
  if (opts.communityUnique) params.set('communityUnique', opts.communityUnique);
  if (opts.ownershipGroup) params.set('ownershipGroup', opts.ownershipGroup);
  if (opts.communityGroup) params.set('communityGroup', opts.communityGroup);
  if (opts.communityId != null) params.set('communityId', String(opts.communityId));
}

function _weeklyQs(opts: WeeklyQsOptions): string {
  const params = new URLSearchParams();
  if (opts.referenceDate) params.set('referenceDate', opts.referenceDate);
  _appendCommonScopeParams(params, opts);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function _monthlyQs(opts: MonthlyQsOptions): string {
  const params = new URLSearchParams();
  if (opts.referenceDate) params.set('referenceDate', opts.referenceDate);
  _appendCommonScopeParams(params, opts);
  if (opts.compare && opts.compare !== 'mom') params.set('compare', opts.compare);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function fetchWeekly(
  clientId: BackendClientId,
  options: WeeklyQsOptions = {},
): Promise<DashboardWeekly> {
  return get<DashboardWeekly>(`/dashboard/${clientId}/weekly${_weeklyQs(options)}`);
}

export function fetchMonthly(
  clientId: BackendClientId,
  options: MonthlyQsOptions = {},
): Promise<DashboardMonthly> {
  return get<DashboardMonthly>(`/dashboard/${clientId}/monthly${_monthlyQs(options)}`);
}

export async function downloadMonthlyPdf(
  clientId: BackendClientId,
  options: MonthlyQsOptions = {},
): Promise<void> {
  const cmpSuffix = options.compare === 'yoy' ? '-yoy' : '';
  return downloadAttachment(
    `/dashboard/${clientId}/monthly.pdf${_monthlyQs(options)}`,
    `${clientId}-monthly${cmpSuffix}-${new Date().toISOString().slice(0, 7)}.pdf`,
  );
}

export async function downloadMonthlyCsv(
  clientId: BackendClientId,
  options: MonthlyQsOptions = {},
): Promise<void> {
  const cmpSuffix = options.compare === 'yoy' ? '-yoy' : '';
  return downloadAttachment(
    `/dashboard/${clientId}/monthly.csv${_monthlyQs(options)}`,
    `${clientId}-monthly${cmpSuffix}-${new Date().toISOString().slice(0, 7)}.csv`,
  );
}

export function fetchTrends(
  clientId: BackendClientId,
  options: {
    weeks?: number;
    portfolio?: string;
    communityUnique?: string;
    ownershipGroup?: string;
    communityGroup?: string;
    communityId?: number;
  } = {},
): Promise<DashboardTrends> {
  const params = new URLSearchParams();
  if (options.weeks) params.set('weeks', String(options.weeks));
  if (options.portfolio) params.set('portfolio', options.portfolio);
  if (options.communityUnique) params.set('communityUnique', options.communityUnique);
  if (options.ownershipGroup) params.set('ownershipGroup', options.ownershipGroup);
  if (options.communityGroup) params.set('communityGroup', options.communityGroup);
  if (options.communityId != null) params.set('communityId', String(options.communityId));
  const qs = params.toString();
  return get<DashboardTrends>(`/dashboard/${clientId}/trends${qs ? `?${qs}` : ''}`);
}

/** Fetch a binary attachment with auth and trigger a browser download. */
async function downloadAttachment(path: string, fallbackName: string): Promise<void> {
  const resp = await authFetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: { ...authHeader() },
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      detail = body.detail || body.error || detail;
    } catch { /* ignore */ }
    throw new Error(`Download failed: ${detail}`);
  }
  const blob = await resp.blob();
  const cd = resp.headers.get('Content-Disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadWeeklyPdf(
  clientId: BackendClientId,
  options: WeeklyQsOptions = {},
): Promise<void> {
  return downloadAttachment(
    `/dashboard/${clientId}/weekly.pdf${_weeklyQs(options)}`,
    `${clientId}-weekly-${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

export async function downloadWeeklyCsv(
  clientId: BackendClientId,
  options: WeeklyQsOptions = {},
): Promise<void> {
  return downloadAttachment(
    `/dashboard/${clientId}/weekly.csv${_weeklyQs(options)}`,
    `${clientId}-weekly-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadCommunitiesCsv(clientId: BackendClientId): Promise<void> {
  return downloadAttachment(
    `/dashboard/${clientId}/communities.csv`,
    `${clientId}-communities-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadMoveInsCsv(
  clientId: BackendClientId,
  windowDays = 30,
): Promise<void> {
  return downloadAttachment(
    `/dashboard/${clientId}/move-ins.csv?windowDays=${windowDays}`,
    `${clientId}-move-ins-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

export async function downloadLeadsCsv(clientId: BackendClientId): Promise<void> {
  return downloadAttachment(
    `/dashboard/${clientId}/leads.csv`,
    `${clientId}-leads-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

// ─── Custom date range ────────────────────────────────────────────────────
export interface CustomQsOptions {
  /** Required — YYYY-MM-DD, inclusive start of the current window. */
  startDate: string;
  /** Required — YYYY-MM-DD, exclusive end of the current window. */
  endDate: string;
  /** Optional override for prior window start (defaults to immediately preceding). */
  compareStartDate?: string;
  /** Optional override for prior window end. */
  compareEndDate?: string;
  portfolio?: string;
  communityUnique?: string;
}

function _customQs(opts: CustomQsOptions): string {
  const params = new URLSearchParams();
  params.set('startDate', opts.startDate);
  params.set('endDate', opts.endDate);
  if (opts.compareStartDate) params.set('compareStartDate', opts.compareStartDate);
  if (opts.compareEndDate) params.set('compareEndDate', opts.compareEndDate);
  if (opts.portfolio) params.set('portfolio', opts.portfolio);
  if (opts.communityUnique) params.set('communityUnique', opts.communityUnique);
  return `?${params.toString()}`;
}

export function fetchCustom(
  clientId: BackendClientId,
  options: CustomQsOptions,
): Promise<DashboardCustom> {
  return get<DashboardCustom>(`/dashboard/${clientId}/custom${_customQs(options)}`);
}

export async function downloadCustomCsv(
  clientId: BackendClientId,
  options: CustomQsOptions,
): Promise<void> {
  return downloadAttachment(
    `/dashboard/${clientId}/custom.csv${_customQs(options)}`,
    `${clientId}-custom-${options.startDate}-to-${options.endDate}.csv`,
  );
}

export async function downloadCustomPdf(
  clientId: BackendClientId,
  options: CustomQsOptions,
): Promise<void> {
  return downloadAttachment(
    `/dashboard/${clientId}/custom.pdf${_customQs(options)}`,
    `${clientId}-custom-${options.startDate}-to-${options.endDate}.pdf`,
  );
}

// ─── Lead source breakdown ────────────────────────────────────────────────
export interface LeadSourceRow {
  source: string;
  leads_current: number;
  leads_prior: number;
  delta: number;
  delta_pct: number | null;
  share_current: number;
  share_prior: number;
}

export interface LeadSources {
  client_id: BackendClientId;
  period: 'week' | 'month';
  current: { start: string; end: string };
  prior: { start: string; end: string };
  snapshot_at: string | null;
  filter: { portfolio: string | null; community_unique: string | null };
  totals: {
    leads_current: number;
    leads_prior: number;
    unique_sources_current: number;
  };
  sources: LeadSourceRow[];
}

export interface LeadSourcesOptions extends WeeklyQsOptions {
  period?: 'week' | 'month';
  /** 'previous' (default) or 'yoy' (same period one year earlier). */
  compare?: 'previous' | 'yoy';
  limit?: number;
}

function _leadSourcesQs(opts: LeadSourcesOptions): string {
  const params = new URLSearchParams();
  if (opts.period) params.set('period', opts.period);
  _appendCommonScopeParams(params, opts);
  if (opts.compare && opts.compare !== 'previous') params.set('compare', opts.compare);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function fetchLeadSources(
  clientId: BackendClientId,
  options: LeadSourcesOptions = {},
): Promise<LeadSources> {
  return get<LeadSources>(
    `/dashboard/${clientId}/leads/sources${_leadSourcesQs(options)}`,
  );
}

export async function downloadLeadSourcesCsv(
  clientId: BackendClientId,
  options: LeadSourcesOptions = {},
): Promise<void> {
  const period = options.period ?? 'week';
  return downloadAttachment(
    `/dashboard/${clientId}/leads/sources.csv${_leadSourcesQs(options)}`,
    `${clientId}-lead-sources-${period}-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

// ─── DWA Creative Scorecard ───────────────────────────────────────────────

export interface ScorecardPulseEntry {
  user_id: string;
  display_name: string;
  score: number;
}

export interface ScorecardAccountExecutive {
  name: string;
  count: number;
}

export interface ScorecardPeriodMetrics {
  snapshot_at: string | null;
  total_projects: number;
  status_counts: {
    on_track: number;
    client_delay: number;
    on_hold: number;
    not_started: number;
  };
  status_pct: {
    on_track: number;
    client_delay: number;
    on_hold: number;
    not_started: number;
  };
  type_counts: {
    web_development: number;
    web_maintenance: number;
    creative: number;
    mix: number;
    media: number;
    internal: number;
  };
  avg_lifespan_days: number;
  time_logged: number;
  available_hours: number;
  time_difference: number;
  workdays: number;
  pulse: {
    average: number | null;
    by_employee: ScorecardPulseEntry[];
  };
  by_account_executive: ScorecardAccountExecutive[];
}

export interface ScorecardPeriod {
  label: string;
  start: string;
  end: string;
  metrics: ScorecardPeriodMetrics;
}

export interface ScorecardResponse {
  client_id: 'dwa-internal';
  period: 'week' | 'month' | 'custom';
  count: number;
  compare?: 'previous' | 'yoy';
  periods: ScorecardPeriod[];
  /** Present when compare='yoy' — parallel periods from one year back. */
  yoy_periods?: ScorecardPeriod[] | null;
  rollup: Omit<ScorecardPeriodMetrics, 'snapshot_at' | 'workdays'> | null;
  yoy_rollup?: Omit<ScorecardPeriodMetrics, 'snapshot_at' | 'workdays'> | null;
}

export interface ScorecardOptions {
  period?: 'week' | 'month';
  count?: number;
  referenceDate?: string;
  /** 'previous' (default) — single set of periods. 'yoy' — also return
   * parallel periods from one year prior under yoy_periods/yoy_rollup. */
  compare?: 'previous' | 'yoy';
}

export interface ScorecardCustomOptions {
  startDate: string;  // YYYY-MM-DD
  endDate: string;    // YYYY-MM-DD
}

function _scorecardQs(opts: ScorecardOptions): string {
  const params = new URLSearchParams();
  if (opts.period) params.set('period', opts.period);
  if (opts.count) params.set('count', String(opts.count));
  if (opts.referenceDate) params.set('referenceDate', opts.referenceDate);
  if (opts.compare && opts.compare !== 'previous') params.set('compare', opts.compare);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function fetchScorecard(
  options: ScorecardOptions = {},
): Promise<ScorecardResponse> {
  return get<ScorecardResponse>(`/dashboard/dwa-internal/scorecard${_scorecardQs(options)}`);
}

export function fetchScorecardCustom(
  options: ScorecardCustomOptions,
): Promise<ScorecardResponse> {
  const params = new URLSearchParams({
    startDate: options.startDate,
    endDate: options.endDate,
  });
  return get<ScorecardResponse>(
    `/dashboard/dwa-internal/scorecard/custom?${params.toString()}`,
  );
}

export async function downloadScorecardPdf(options: ScorecardOptions = {}): Promise<void> {
  const period = options.period ?? 'week';
  return downloadAttachment(
    `/dashboard/dwa-internal/scorecard.pdf${_scorecardQs(options)}`,
    `dwa-creative-scorecard-${period}-${new Date().toISOString().slice(0, 10)}.pdf`,
  );
}

export async function downloadScorecardCsv(options: ScorecardOptions = {}): Promise<void> {
  const period = options.period ?? 'week';
  return downloadAttachment(
    `/dashboard/dwa-internal/scorecard.csv${_scorecardQs(options)}`,
    `dwa-creative-scorecard-${period}-${new Date().toISOString().slice(0, 10)}.csv`,
  );
}

// ─── Pulse scores ─────────────────────────────────────────────────────────

export interface PulseScore {
  id: number;
  user_id: string;
  display_name: string;
  email: string | null;
  score: number;
  note: string | null;
  submitted_at: string | null;
  submitted_by_user_id: string | null;
}

export interface PulseListResponse {
  client_id: 'dwa-internal';
  week_start_monday: string;
  scores: PulseScore[];
}

export interface PulseUpsertRequest {
  score: number;
  week_start_monday: string;
  user_id?: string;
  note?: string;
}

export function fetchPulseForWeek(weekStartMonday?: string): Promise<PulseListResponse> {
  const qs = weekStartMonday ? `?weekStart=${encodeURIComponent(weekStartMonday)}` : '';
  return get<PulseListResponse>(`/dashboard/dwa-internal/pulse${qs}`);
}

export async function submitPulse(body: PulseUpsertRequest): Promise<void> {
  const resp = await authFetch(`${API_BASE}/dashboard/dwa-internal/pulse`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const errBody = await resp.json();
      detail = errBody.detail || errBody.error || detail;
    } catch { /* ignore */ }
    throw new Error(`Pulse submit failed: ${detail}`);
  }
}

// ─── Team Capacity (Operations) ───────────────────────────────────────────
// Per-person utilization from Teamwork time entries. dwa-internal only.

export interface TeamCapacityPerson {
  user_id: string | null;
  name: string;
  logged_hours: number;
  billable_hours: number;
  target_hours: number;
  /** logged / target (ratio, e.g. 0.85 = 85%). */
  utilization: number;
  /** billable / target (ratio). */
  billable_utilization: number;
}

export interface TeamCapacityMetrics {
  snapshot_at: string | null;
  workdays: number;
  per_person_target_hours: number;
  headcount: number;
  team: {
    logged_hours: number;
    billable_hours: number;
    target_hours: number;
    utilization: number;
    billable_utilization: number;
  };
  by_person: TeamCapacityPerson[];
}

export interface TeamCapacityPeriod {
  label: string;
  start: string;
  end: string;
  metrics: TeamCapacityMetrics;
}

export interface TeamCapacityResponse {
  client_id: 'dwa-internal';
  period: 'week' | 'month';
  count: number;
  /** Newest period first — periods[0] is the most recent window. */
  periods: TeamCapacityPeriod[];
}

export interface TeamCapacityOptions {
  period?: 'week' | 'month';
  count?: number;
  referenceDate?: string;
}

function _teamCapacityQs(opts: TeamCapacityOptions): string {
  const params = new URLSearchParams();
  if (opts.period) params.set('period', opts.period);
  if (opts.count) params.set('count', String(opts.count));
  if (opts.referenceDate) params.set('referenceDate', opts.referenceDate);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function fetchTeamCapacity(
  options: TeamCapacityOptions = {},
): Promise<TeamCapacityResponse> {
  return get<TeamCapacityResponse>(
    `/dashboard/dwa-internal/team-capacity${_teamCapacityQs(options)}`,
  );
}

// ─── Team Capacity — Upcoming forecast (Phase 2) ───────────────────────────

export type ForecastGranularity = 'day' | 'week';
export type ForecastSource = 'task_load' | 'booked';

export interface ForecastCell {
  planned_hours: number;
  capacity_hours: number;
  available_hours: number;
  utilization: number;
}

export interface ForecastPeriod {
  label: string;
  start: string;
  end: string;
  capacity_per_person: number;
  team: {
    planned_hours: number;
    capacity_hours: number;
    available_hours: number;
    utilization: number;
  };
  /** Keyed by user_id → that person's cell for this period. */
  by_person: Record<string, ForecastCell>;
}

export interface ForecastRosterEntry {
  user_id: string;
  name: string;
}

export interface TeamCapacityForecast {
  client_id: 'dwa-internal';
  granularity: ForecastGranularity;
  source: ForecastSource;
  generated_for: string;
  snapshot_at: string | null;
  roster: ForecastRosterEntry[];
  periods: ForecastPeriod[];
  coverage: {
    has_data?: boolean;
    people_with_data?: number;
    roster_size?: number;
    // task_load
    task_day_rows?: number;
    total_tasks?: number;
    // booked
    allocation_count?: number;
  };
}

export interface ForecastOptions {
  granularity?: ForecastGranularity;
  source?: ForecastSource;
}

export function fetchTeamCapacityForecast(
  options: ForecastOptions = {},
): Promise<TeamCapacityForecast> {
  const params = new URLSearchParams();
  if (options.granularity) params.set('granularity', options.granularity);
  if (options.source) params.set('source', options.source);
  const qs = params.toString();
  return get<TeamCapacityForecast>(
    `/dashboard/dwa-internal/team-capacity/forecast${qs ? `?${qs}` : ''}`,
  );
}

// ─── Web Monitor (DWA Internal → Web): Blog Monitor + SmashBalloon ──────────

/** Clients with a monitored-site registry. The dashboard's client selector
 * chooses one; more get added as their registries are seeded. */
export const WEB_MONITOR_CLIENTS: { key: BackendClientId; label: string }[] = [
  { key: 'plc', label: 'Priority Life Care' },
];

export interface BlogMonitorSite {
  site_id: number;
  name: string;
  domain: string;
  homepage_url: string | null;
  community_group: string | null;
  ownership_group: string | null;
  care_levels: string | null;
  respite: boolean;
  /** ok | no_posts | error | unreachable | pending */
  status: string;
  http_status: number | null;
  latest_post_title: string | null;
  latest_post_url: string | null;
  latest_post_at: string | null;
  post_count: number | null;
  error: string | null;
}

export interface BlogMonitorResponse {
  client_id: BackendClientId;
  snapshot_at: string | null;
  site_count: number;
  sites: BlogMonitorSite[];
}

export function fetchBlogMonitor(clientId: BackendClientId): Promise<BlogMonitorResponse> {
  return get<BlogMonitorResponse>(`/dashboard/${clientId}/web/blog-monitor`);
}

export interface SmashBalloonSite {
  site_id: number;
  name: string;
  domain: string;
  homepage_url: string | null;
  community_group: string | null;
  ownership_group: string | null;
  care_levels: string | null;
  respite: boolean;
  /** ok | empty | not_found | unreachable | pending */
  status: string;
  http_status: number | null;
  feed_detected: boolean | null;
  post_count: number | null;
  latest_post_at: string | null;
  error: string | null;
}

export interface SmashBalloonResponse {
  client_id: BackendClientId;
  snapshot_at: string | null;
  site_count: number;
  sites: SmashBalloonSite[];
}

export function fetchSmashBalloon(clientId: BackendClientId): Promise<SmashBalloonResponse> {
  return get<SmashBalloonResponse>(`/dashboard/${clientId}/web/smashballoon`);
}

// ─── Backup Monitor (DWA Internal → Web) ──────────────────────────────────

/** ok | failed | incomplete | stale — computed server-side (backup_monitor.assess). */
export type BackupHealth = 'ok' | 'failed' | 'incomplete' | 'stale';

export interface BackupRun {
  run_id: string;
  /** success | failed */
  status: string;
  exit_code: number | null;
  started_at: string | null;
  finished_at: string | null;
  received_at: string;
  source_count: number | null;
  source_bytes: number | null;
  backup_count: number | null;
  backup_bytes: number | null;
}

export interface BackupJob {
  client: string;
  job: string;
  client_name: string;
  description: string | null;
  schedule: string | null;
  stale_after_hours: number;
  health: BackupHealth;
  reasons: string[];
  latest: BackupRun | null;
  /** Most recent first, latest included. */
  history: BackupRun[];
}

export interface BackupMonitorResponse {
  checked_at: string;
  job_count: number;
  jobs: BackupJob[];
}

export function fetchBackupMonitor(): Promise<BackupMonitorResponse> {
  return get<BackupMonitorResponse>('/dashboard/dwa-internal/web/backups');
}

// ─── Banner Manager (Web) ──────────────────────────────────────────────────

async function send<T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const resp = await authFetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try { const b = await resp.json(); detail = b.detail || b.error || detail; } catch { /* ignore */ }
    throw new Error(`${method} ${path} failed: ${detail}`);
  }
  return resp.json() as Promise<T>;
}

// ─── Client Database (DWA Internal → Accounts) ──────────────────────────────

export type CommunityLifecycle =
  | 'prospective' | 'onboarding' | 'in_development' | 'live'
  | 'offboarding' | 'inactive' | 'listing_only' | 'non_community';

/** Fields the dashboard can write in v1. The rest of the ROT record is
 *  displayed read-only and seeded by scripts/import_plc_rot.py. */
export interface CommunityInput {
  name: string;
  domain?: string | null;
  ownership_group?: string | null;
  community_group?: string | null;
  community_template?: string | null;
  site_type?: string | null;
  lifecycle_status: CommunityLifecycle;
  target_launch_date?: string | null;  // YYYY-MM-DD
  monitoring_enabled: boolean;
  server?: string | null;
  wpengine_install?: string | null;
  wh_comm_id?: string | null;
  care_levels?: string | null;
  main_phone?: string | null;
  street_address?: string | null;
  state?: string | null;
  licensing_number?: string | null;
  google_maps_url?: string | null;
  facebook_url?: string | null;
  tso_calendar_url?: string | null;
  brochure_url?: string | null;
  schedule_tour_url?: string | null;
  connect_sales_url?: string | null;
  contact_us_url?: string | null;
  drive_url?: string | null;
  notes?: string | null;
}

/** Read-only fields, seeded from the ROT. */
export interface Community extends CommunityInput {
  id: number;
  status_raw?: string | null;
  launched_at?: string | null;
  respite?: boolean | null;
  mw?: boolean | null;
  lihtc?: boolean | null;
  mc_epc?: boolean | null;
  legally_must_show?: boolean | null;
  ada_equal_housing?: string | null;
  has_playbook?: boolean | null;
  ads_enabled?: boolean | null;
  search_enabled?: boolean | null;
  pmax_enabled?: boolean | null;
  click_cease_enabled?: boolean | null;
  retargeting_enabled?: boolean | null;
  facebook_ads_enabled?: boolean | null;
}

export interface CommunityListResponse {
  client_id: BackendClientId;
  communities: Community[];
  counts: Record<string, number>;
  choices: { templates: string[]; groups: string[]; lifecycle: CommunityLifecycle[] };
  /** WelcomeHome ids used by more than one community — ambiguous CRM binding. */
  duplicate_wh_ids: Record<string, string[]>;
}

export function fetchClientCommunities(clientId: BackendClientId): Promise<CommunityListResponse> {
  return get<CommunityListResponse>(`/dashboard/${clientId}/accounts/communities`);
}

export function createClientCommunity(clientId: BackendClientId, input: CommunityInput): Promise<Community> {
  return send<Community>('POST', `/dashboard/${clientId}/accounts/communities`, input);
}

export function updateClientCommunity(clientId: BackendClientId, id: number, input: CommunityInput): Promise<Community> {
  return send<Community>('PUT', `/dashboard/${clientId}/accounts/communities/${id}`, input);
}

export function markClientCommunityLive(clientId: BackendClientId, id: number): Promise<Community> {
  return send<Community>('POST', `/dashboard/${clientId}/accounts/communities/${id}/mark-live`);
}

export function deleteClientCommunity(clientId: BackendClientId, id: number): Promise<{ deleted: number }> {
  return send<{ deleted: number }>('DELETE', `/dashboard/${clientId}/accounts/communities/${id}`);
}

// ─── Client Brands (DWA Internal → Accounts, mirrored under Creative) ───────

/** Clients with a brand list. Every tenant has the table (migration 029). */
/** Clients that can serve banners — mirrors dashboard_routes.BANNER_CLIENTS.
 *  Separate from WEB_MONITOR_CLIENTS: DWA runs banners on its own site but has
 *  no blog/SmashBalloon registry to monitor. */
export const BANNER_CLIENTS: { key: BackendClientId; label: string }[] = [
  { key: 'plc', label: 'Priority Life Care' },
  { key: 'dwa-internal', label: 'Digital Wolf Agency' },
];

export const BRAND_CLIENTS: { key: BackendClientId; label: string }[] = [
  { key: 'plc', label: 'Priority Life Care' },
  { key: 'morningstar', label: 'MorningStar Senior Living' },
  { key: 'dwa-internal', label: 'Digital Wolf Agency' },
];

export type BrandKind = 'brand' | 'sub_brand';

/** Editable brand fields. Null colors inherit — parent brand, then the global
 *  banner defaults — so a sub-brand only stores what it actually overrides. */
export interface BrandInput {
  name: string;
  kind: BrandKind;
  parent_id?: number | null;
  /** ROT 'Community Template' — the join key from a site to its brand. */
  template_key?: string | null;
  /** ROT 'Community Group' — ownership/org dimension, reference only. */
  group_key?: string | null;
  bg_color?: string | null;
  text_color?: string | null;
  button_color?: string | null;
  button_hover_color?: string | null;
  button_text_color?: string | null;
  button_text_hover_color?: string | null;
  /** Gradient background. Both stops required, else the flat bg_color is used. */
  bg_gradient_from?: string | null;
  /** Optional third stop at the centre — null means a two-colour gradient. */
  bg_gradient_mid?: string | null;
  bg_gradient_to?: string | null;
  /** Degrees; defaults to 180 (top → bottom) when a gradient is set. */
  bg_gradient_angle?: number | null;
  notes?: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface Brand extends BrandInput {
  id: number;
  /** Registered (live) sites on this brand's template. 0 while in development. */
  site_count: number;
}

export interface BrandListResponse {
  client_id: BackendClientId;
  brands: Brand[];
  sites_without_template: number;
  /** Valid dropdown values — registry values unioned with those already in use. */
  choices: { templates: string[]; groups: string[] };
}

export function fetchBrands(clientId: BackendClientId): Promise<BrandListResponse> {
  return get<BrandListResponse>(`/dashboard/${clientId}/brands`);
}

export function createBrand(clientId: BackendClientId, input: BrandInput): Promise<Brand> {
  return send<Brand>('POST', `/dashboard/${clientId}/brands`, input);
}

export function updateBrand(clientId: BackendClientId, id: number, input: BrandInput): Promise<Brand> {
  return send<Brand>('PUT', `/dashboard/${clientId}/brands/${id}`, input);
}

export function deleteBrand(clientId: BackendClientId, id: number): Promise<{ deleted: number }> {
  return send<{ deleted: number }>('DELETE', `/dashboard/${clientId}/brands/${id}`);
}

export type BannerTargetType = 'all' | 'group' | 'site';
export type BannerStatus = 'active' | 'scheduled' | 'expired' | 'disabled';

/** Editable banner fields (create/update payload). */
export interface BannerInput {
  name: string;
  target_type: BannerTargetType;
  target_group?: string | null;
  target_site_id?: number | null;
  headline?: string | null;
  message?: string | null;
  button_label?: string | null;
  button_url?: string | null;
  bg_color?: string | null;
  text_color?: string | null;
  button_color?: string | null;
  button_hover_color?: string | null;  // null = no hover change
  button_text_color?: string | null;  // null = renderer default (#111)
  button_text_hover_color?: string | null;  // null = no hover change
  /** Gradient background. Both stops required, else the flat bg_color is used. */
  bg_gradient_from?: string | null;
  /** Optional third stop at the centre — null means a two-colour gradient. */
  bg_gradient_mid?: string | null;
  bg_gradient_to?: string | null;
  /** Degrees; defaults to 180 (top → bottom) when a gradient is set. */
  bg_gradient_angle?: number | null;
  button_new_tab: boolean;
  dismissible: boolean;
  start_at?: string | null;  // ISO 8601 UTC
  end_at?: string | null;
  is_enabled: boolean;
}

export interface Banner extends BannerInput {
  id: number;
  status: BannerStatus;
  preview_html: string;
  target_site_name?: string | null;
}

/** Brand colors for one template. Only colors the brand actually sets appear. */
export interface BrandPreset {
  brand_name: string;
  bg_color?: string | null;
  text_color?: string | null;
  button_color?: string | null;
  button_hover_color?: string | null;
  button_text_color?: string | null;
  button_text_hover_color?: string | null;
  bg_gradient_from?: string | null;
  bg_gradient_mid?: string | null;
  bg_gradient_to?: string | null;
  bg_gradient_angle?: number | null;
}

export interface BannerListResponse {
  client_id: BackendClientId;
  banners: Banner[];
  targets: {
    groups: string[];
    sites: {
      id: number;
      name: string;
      community_group?: string | null;
      /** Maps the site to its brand — the preset lookup key. */
      community_template?: string | null;
    }[];
  };
  /** Keyed by community_template. */
  brand_presets: Record<string, BrandPreset>;
}

export function fetchBanners(clientId: BackendClientId): Promise<BannerListResponse> {
  return get<BannerListResponse>(`/dashboard/${clientId}/web/banners`);
}

export function createBanner(clientId: BackendClientId, input: BannerInput): Promise<Banner> {
  return send<Banner>('POST', `/dashboard/${clientId}/web/banners`, input);
}

export function updateBanner(clientId: BackendClientId, id: number, input: BannerInput): Promise<Banner> {
  return send<Banner>('PUT', `/dashboard/${clientId}/web/banners/${id}`, input);
}

export function deleteBanner(clientId: BackendClientId, id: number): Promise<{ deleted: number }> {
  return send<{ deleted: number }>('DELETE', `/dashboard/${clientId}/web/banners/${id}`);
}

// ─── HubSpot (DWA Internal → Growth) ───────────────────────────────────────
// Current-state copy of DWA's own HubSpot (migration 037), computed server-side
// by services/agent/app/hubspot_dashboard.py. One payload feeds all five
// Growth dashboards.

export interface HubSpotBucket {
  count: number;
  amount: number;
  weighted: number;
}

export interface HubSpotStageRow extends HubSpotBucket {
  stage_id: string | null;
  label: string;
  probability: number | null;
}

export interface HubSpotPipeline extends HubSpotBucket {
  pipeline_id: string;
  label: string;
  stages: HubSpotStageRow[];
}

export interface HubSpotDealBrief {
  deal_id: string;
  name: string;
  pipeline: string | null;
  stage: string | null;
  amount: number | null;
  owner: string;
  close_date: string | null;
  days_in_stage: number | null;
  days_since_modified: number | null;
  issues?: string[];
}

export interface HubSpotSyncRun {
  status: 'running' | 'success' | 'partial' | 'failed' | 'skipped';
  started_at: string | null;
  finished_at: string | null;
  counts: Record<string, number>;
  details: { activity_unavailable?: string[]; errors?: Record<string, string>; reason?: string };
}

export type HubSpotActivityType = 'calls' | 'meetings' | 'emails' | 'notes' | 'tasks';

export interface HubSpotOwnerRow {
  owner_id: string | null;
  name: string;
  open_count: number;
  open_amount: number;
  weighted: number;
  won_quarter_count: number;
  won_quarter_amount: number;
  created_month: number;
  activities: Record<HubSpotActivityType, number>;
  activity_total: number;
}

export type HubSpotHygieneCheck =
  | 'missing_amount' | 'missing_close_date' | 'missing_owner' | 'past_due' | 'no_company';

export interface HubSpotDashboardResponse {
  generated_at: string;
  has_data: boolean;
  sync: { latest: HubSpotSyncRun | null; last_success: HubSpotSyncRun | null };
  activity_unavailable: HubSpotActivityType[];
  pipeline: {
    pipelines: HubSpotPipeline[];
    totals: HubSpotBucket;
    closing_this_month: HubSpotBucket;
    closing_this_quarter: HubSpotBucket;
    stalled: HubSpotBucket & { days: number; deals: HubSpotDealBrief[] };
  };
  leads: {
    weeks: { week_start: string; contacts: number; companies: number }[];
    since: string;
    totals: { contacts: number; companies: number };
    by_source: { source: string | null; label: string; contacts: number; companies: number }[];
    by_lifecycle: { stage: string; label: string; contacts: number }[];
  };
  win_loss: {
    months: { month: string; label: string; won: number; lost: number; won_amount: number; lost_amount: number }[];
    since: string;
    totals: {
      won: number;
      lost: number;
      won_amount: number;
      win_rate: number | null;
      avg_won_amount: number | null;
      avg_cycle_days: number | null;
    };
  };
  owners: { owners: HubSpotOwnerRow[]; activity_days: number; activity_available: boolean };
  funnel: {
    stages: { stage: string; label: string; count: number; reached: number; conversion: number | null }[];
    other: number;
    none: number;
    total: number;
  };
  hygiene: {
    open_deals: number;
    deal_checks: Record<HubSpotHygieneCheck, number>;
    contacts_no_owner: number;
    companies_no_owner: number;
    contacts_total: number;
    companies_total: number;
    deals: HubSpotDealBrief[];
  };
}

export function fetchHubSpotDashboard(stalledDays = 30): Promise<HubSpotDashboardResponse> {
  return get<HubSpotDashboardResponse>(`/dashboard/dwa-internal/hubspot?stalled_days=${stalledDays}`);
}

/** Admin only (403 otherwise). Queues the read-only HubSpot sync on the worker. */
export function triggerHubSpotSync(): Promise<{ queued: boolean; task_id: string }> {
  return send<{ queued: boolean; task_id: string }>('POST', '/dashboard/dwa-internal/hubspot/sync');
}
